import { useEffect } from 'react';

/**
 * Dependency-free per-page SEO. Updates <title>, meta description, canonical,
 * and Open Graph / Twitter tags on mount. Works without SSR because modern
 * crawlers (Googlebot, Bingbot) render client-side JavaScript before indexing.
 */
const SITE_NAME = 'ArtSyde';
const SITE_URL = 'https://artsyde-312ed.web.app';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const TWITTER_HANDLE = '@ArtSyde';
const DEFAULT_DESCRIPTION =
    'The social marketplace for artists. Discover original art, follow creators, and buy & sell directly with the community.';

function upsertMeta(attr, key, content) {
    if (content == null || content === '') return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function upsertLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

export default function Seo({ title, description, image, type = 'website', noindex = false, jsonLd }) {
    useEffect(() => {
        const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Discover, Share & Sell Original Art`;
        const desc = description || DEFAULT_DESCRIPTION;
        const img = image || DEFAULT_IMAGE;
        const url = window.location.origin + window.location.pathname + window.location.search;

        document.title = fullTitle;
        upsertMeta('name', 'description', desc);
        upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
        upsertLink('canonical', url);

        upsertMeta('property', 'og:site_name', SITE_NAME);
        upsertMeta('property', 'og:title', fullTitle);
        upsertMeta('property', 'og:description', desc);
        upsertMeta('property', 'og:type', type);
        upsertMeta('property', 'og:url', url);
        upsertMeta('property', 'og:image', img);
        upsertMeta('property', 'og:image:alt', title || SITE_NAME);

        // Twitter / X (Discord also reads the twitter:card hint for large images)
        upsertMeta('name', 'twitter:card', 'summary_large_image');
        upsertMeta('name', 'twitter:site', TWITTER_HANDLE);
        upsertMeta('name', 'twitter:creator', TWITTER_HANDLE);
        upsertMeta('name', 'twitter:title', fullTitle);
        upsertMeta('name', 'twitter:description', desc);
        upsertMeta('name', 'twitter:image', img);
        upsertMeta('name', 'twitter:image:alt', title || SITE_NAME);

        // Optional per-page JSON-LD structured data.
        let scriptEl = null;
        if (jsonLd) {
            scriptEl = document.createElement('script');
            scriptEl.type = 'application/ld+json';
            scriptEl.setAttribute('data-seo', 'page');
            scriptEl.textContent = JSON.stringify(jsonLd);
            document.head.appendChild(scriptEl);
        }

        return () => {
            if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
        };
    }, [title, description, image, type, noindex, jsonLd]);

    return null;
}
